/**
 * The Height breach report (MB's R196, 4 Nov 2026; docs/specs/geosoft-qc.md §27). When Flight data QC finds a line
 * below the lower working height or the hard deck, the crew records why in a Word document kept beside the JSA and
 * JSP, `Documents\Job Specific Documents\Height_breach\<job>_Working_Height_Breach_Report_<ddmmyyyy>.docx` and `.pdf`,
 * one growing report per project: each breach adds an entry, and the report is saved again with today's date. MB's
 * sample (F26044 Day 307 Flt004, line 10046.0) is `tests/fixtures/f26044/height-breach/`.
 *
 * Each entry, as MB decided it (4 Nov 2026, from his sample):
 *
 *     Day 307 Flt004, Line: 10046.0 (Duration: 8 Fids, Distance: 21m, Min: 38.11m)
 *     Local Time: 13:47, Time Since Airborne: 3.0hrs, Ground Speed: 116.6 kts
 *     (East: 427595.95 North: 8123683.19) zFid 11351150 to 11351500
 *     Reason – delayed reaction to terrain profile during contour. Corrected immediately, no risk to pilot safety.
 *     <the chart of the line's heights and the breach>
 *
 * Where each value comes from (MB's words, checked on the genuine F26044 `Flt04.gdb`):
 * - Duration: the fids of the breach's zFid range whose radalt (the channel the QC checked: `RadAlt_mED` on a C208,
 *   `Radalt_ED` on a C210 or helicopter) is below the height: 8 at 20 Hz.
 * - Distance: the breach's distance Flight data QC computed (21.1 m), to the whole metre.
 * - Min: the lowest radalt of the breach, two decimals.
 * - East, North (MB's decision, 4 Nov 2026): the position at the lowest point of the breach, the family's interpolated
 *   coordinates (`Easting_ED`, `Northing_ED` on a C208) at the lowest radalt's fid: L100460 427595.95, 8123683.19 at
 *   11351250. (MB had typed the raw 2 Hz fix at the breach's end, 427580.86, 8123683.51.)
 * - Local Time: `Time` at the lowest point, between its 2 Hz fixes (13:47:11.1). `Time` is seconds past midnight on the Zdas clock, which is UTC+8 on every Thomson system (WA and Queensland,
 *   fixed wing and helicopter: `Time` − `TimeU` is 8 h in every XYZ under `C:\TA_Survey`), as the log's take-off is.
 *   The local time shown is the project's (decisions 19 and 20): `TimeU` plus the project's UTC offset, which is
 *   `Time` itself at UTC+8. To the minute, as a clock shows it (13:47:11.5 is 13:47).
 * - Time Since Airborne: the local time less the take-off time, both to the minute as the report writes them (13:47 −
 *   10:50 = 2 h 57 min), in hours to one decimal, a half up (2.95 is 3.0). The take-off is the flight's daily entry's
 *   (as the crew typed it), else the log's.
 * - Ground Speed: the first `Knots2D` reading (1 Hz) from the lowest point on: L100460 116.6 at 11352000 (117.0 is the
 *   reading before, at 11351000), as MB's report has it.
 *
 * The breach's channels are the whole line's (the report's chart draws it, `breachChart` in Teslyn Field).
 *
 * Nothing here reads a file: Teslyn Field reads the breach's channels from a copy of the flight database
 * (`teslyn_geosoft::breach`, the rows below) and writes the documents (`teslyn_core::report::height_breach`).
 */
import { qcFamilyProfile, qcLogLine, type QcEvent, type QcFamily } from './geosoft-qc.js';
import type { DailyEntry, Flight, Project } from './model.js';
import { qcChannelLabel } from './qc-height-sources.js';
import { zfidRangeText } from './zfid.js';

/** The two Flight data QC tests that make a height breach. */
export const HEIGHT_BREACH_TESTS = ['lower_working_height', 'hard_deck'] as const;
export type HeightBreachKind = (typeof HEIGHT_BREACH_TESTS)[number];

export function isHeightBreachTest(key: string): key is HeightBreachKind {
  return (HEIGHT_BREACH_TESTS as readonly string[]).includes(key);
}

/** The Zdas channels every Thomson database has (C208, C210 and helicopter XYZ alike). */
export const BREACH_TIME_CHANNEL = 'Time';
export const BREACH_TIME_UTC_CHANNEL = 'TimeU';
export const BREACH_SPEED_CHANNEL = 'Knots2D';
/** The Zdas clock's hours from UTC (`Time` − `TimeU` in every database on file). */
export const ZDAS_CLOCK_UTC_OFFSET_H = 8;
/** The report's zoomed panel shows this many seconds either side of the breach (MB's approved chart). */
export const BREACH_ZOOM_S = 5;
/** The GPS height channel the chart draws (every family's). */
export const BREACH_GPS_HEIGHT_CHANNEL = 'GpsHt_ED';
/** The longest reason (as Acknowledge & accepted's). */
export const BREACH_REASON_MAX = 500;
/** The document's name after the job: `<job>_Working_Height_Breach_Report_<ddmmyyyy>` (MB's sample). */
export const BREACH_REPORT_SUFFIX = '_Working_Height_Breach_Report_';
/** The folder under the project folder the report is kept in. */
export const BREACH_REPORT_FOLDER = ['Documents', 'Job Specific Documents', 'Height_breach'] as const;
/** The manifest schema Teslyn Field's core renders (`teslyn_core::report::height_breach::SCHEMA`). */
export const BREACH_REPORT_SCHEMA = 'teslyn.height-breach.v1';

/**
 * The channels to read for a family's breach: the QC's radalt and coordinates (the interpolated ones the QC uses:
 * `Easting_ED`, `Northing_ED` on a C208), the GPS height, and the Zdas time and speed.
 */
export function heightBreachChannels(family: QcFamily): {
  readonly radalt: string;
  readonly east: string;
  readonly north: string;
  readonly all: readonly string[];
} {
  const p = qcFamilyProfile(family);
  return {
    radalt: p.radalt,
    east: p.x,
    north: p.y,
    all: [
      p.radalt,
      BREACH_GPS_HEIGHT_CHANNEL,
      BREACH_TIME_CHANNEL,
      BREACH_TIME_UTC_CHANNEL,
      BREACH_SPEED_CHANNEL,
      p.x,
      p.y,
    ],
  };
}

/** The breach's fiducials as Teslyn Field reads them (`teslyn_geosoft::breach::BreachRows`). */
export interface HeightBreachRows {
  readonly line_name: string;
  readonly fids: readonly number[];
  readonly channels: readonly { readonly channel: string; readonly values: readonly (number | null)[] }[];
  /** Channels the database has not got. */
  readonly missing: readonly string[];
}

/** One breach's facts, as kept with its entry (snake case: stored as JSON in Teslyn Field's database). */
export interface HeightBreachFacts {
  readonly kind: HeightBreachKind;
  /** The height the QC checked against (the run's), metres. */
  readonly threshold_m: number;
  readonly jday: number;
  readonly flight_number: number;
  /** The Geosoft line name (`L100460`). */
  readonly line_name: string;
  readonly fid_start: number;
  readonly fid_end: number;
  readonly duration_fids: number;
  readonly distance_m: number;
  readonly min_m: number;
  readonly min_fid: number;
  /** The radalt channel the minimum is from. */
  readonly radalt_channel: string;
  /** `13:47`: the project's local time at the lowest point. */
  readonly local_time: string | null;
  /** `10:50`: the take-off used. */
  readonly takeoff: string | null;
  readonly airborne_h: number | null;
  readonly ground_speed_kts: number | null;
  readonly east: number | null;
  readonly north: number | null;
}

export interface HeightBreachFactsInput {
  readonly kind: HeightBreachKind;
  readonly thresholdM: number;
  readonly event: Pick<QcEvent, 'fid_start' | 'fid_end' | 'length_m'>;
  readonly rows: HeightBreachRows;
  readonly family: QcFamily;
  readonly jday: number;
  readonly flightNumber: number;
  readonly lineName: string;
  /** The daily entry's take-off (`HH:MM`, as typed), if any. */
  readonly entryTakeoff?: string | null;
  /** The log's take-off (`YYYY-MM-DDTHH:MM:SS`, local), if any. */
  readonly logTakeoff?: string | null;
  /** The project's hours from UTC (`projectUtcOffsetH`). */
  readonly utcOffsetH: number;
}

const column = (rows: HeightBreachRows, name: string): readonly (number | null)[] | null => {
  const c = rows.channels.find((x) => x.channel.toLowerCase() === name.toLowerCase());
  return c && c.values.length === rows.fids.length ? c.values : null;
};

/**
 * A channel's value at the fid of row `at`: its own reading there, else the line between the readings either side of
 * it (a 2 Hz channel such as `Time` between its fixes), else the nearest reading. Null when it has none.
 */
export function valueAtRow(
  fids: readonly number[],
  values: readonly (number | null)[],
  at: number,
): number | null {
  const ok = (i: number): boolean => {
    const v = values[i];
    return v !== null && v !== undefined && Number.isFinite(v);
  };
  if (ok(at)) return values[at]!;
  let before = -1;
  for (let i = at - 1; i >= 0; i--)
    if (ok(i)) {
      before = i;
      break;
    }
  let after = -1;
  for (let i = at + 1; i < fids.length; i++)
    if (ok(i)) {
      after = i;
      break;
    }
  if (before >= 0 && after >= 0) {
    const share = (fids[at]! - fids[before]!) / (fids[after]! - fids[before]! || 1);
    return values[before]! + (values[after]! - values[before]!) * share;
  }
  if (before >= 0) return values[before]!;
  return after >= 0 ? values[after]! : null;
}

/** The first reading of `values` from row `at` on, else the last before it. Null when it has none. */
export function readingFrom(values: readonly (number | null)[], at: number): number | null {
  const ok = (v: number | null | undefined): v is number =>
    v !== null && v !== undefined && Number.isFinite(v);
  for (let i = at; i < values.length; i++) if (ok(values[i])) return values[i]!;
  for (let i = at - 1; i >= 0; i--) if (ok(values[i])) return values[i]!;
  return null;
}

/** `HH:MM` of seconds past midnight (any day: wrapped into 0 to 24 h), truncated to the minute as a clock shows. */
export function clockText(seconds: number): string {
  const s = ((Math.floor(seconds) % 86400) + 86400) % 86400;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Minutes past midnight of `HH:MM` or `…THH:MM[:SS]`; null when it is not a time. */
export function clockMinutes(text: string | null | undefined): number | null {
  const m = /(?:^|T)(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec((text ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

/**
 * Hours from take-off to the breach, both to the minute as the report writes them, to one decimal with a half up
 * (13:47 − 10:50 = 177 min = 2.95 h → 3.0). A breach after midnight counts on from the take-off.
 */
export function airborneHours(localTime: string, takeoff: string): number | null {
  const a = clockMinutes(localTime);
  const t = clockMinutes(takeoff);
  if (a === null || t === null) return null;
  const minutes = (((a - t) % 1440) + 1440) % 1440;
  return Math.floor(minutes / 6 + 0.5) / 10;
}

const round2 = (v: number | null): number | null => (v === null ? null : Math.round(v * 100) / 100);

/** The facts of one breach from its rows, or why they cannot be had. */
export function heightBreachFacts(
  input: HeightBreachFactsInput,
): { readonly facts: HeightBreachFacts } | { readonly error: string } {
  const { rows, event } = input;
  const radaltName = heightBreachChannels(input.family).radalt;
  const radalt = column(rows, radaltName);
  if (!radalt) return { error: `The flight database has no ${radaltName} on ${input.lineName}.` };
  const inBreach = (f: number): boolean => f >= event.fid_start && f <= event.fid_end;
  let minRow: number | null = null;
  let duration = 0;
  for (let i = 0; i < rows.fids.length; i++) {
    const v = radalt[i];
    if (!inBreach(rows.fids[i]!) || v === null || v === undefined) continue;
    if (v < input.thresholdM) duration += 1;
    if (minRow === null || v < radalt[minRow]!) minRow = i;
  }
  if (minRow === null)
    return {
      error: `${input.lineName} has no ${radaltName} reading in ${zfidRangeText(event.fid_start, event.fid_end)}.`,
    };
  const minFid = rows.fids[minRow]!;
  const lowest = minRow;
  const at = (name: string): number | null => {
    const values = column(rows, name);
    return values ? valueAtRow(rows.fids, values, lowest) : null;
  };
  const channels = heightBreachChannels(input.family);
  const speed = column(rows, BREACH_SPEED_CHANNEL);
  // the project's clock: TimeU and the project's offset; Time is the Zdas clock, UTC+8
  const utc = at(BREACH_TIME_UTC_CHANNEL);
  const zdas = at(BREACH_TIME_CHANNEL);
  const local =
    utc !== null
      ? utc + input.utcOffsetH * 3600
      : zdas !== null
        ? zdas + (input.utcOffsetH - ZDAS_CLOCK_UTC_OFFSET_H) * 3600
        : null;
  const localTime = local === null ? null : clockText(local);
  const entry = clockMinutes(input.entryTakeoff);
  const log = clockMinutes(input.logTakeoff);
  const takeoffMin = entry ?? log;
  const takeoff = takeoffMin === null ? null : clockText(takeoffMin * 60);
  return {
    facts: {
      kind: input.kind,
      threshold_m: input.thresholdM,
      jday: input.jday,
      flight_number: input.flightNumber,
      line_name: input.lineName,
      fid_start: event.fid_start,
      fid_end: event.fid_end,
      duration_fids: duration,
      distance_m: event.length_m,
      min_m: radalt[minRow]!,
      min_fid: minFid,
      radalt_channel: radaltName,
      local_time: localTime,
      takeoff,
      airborne_h: localTime && takeoff ? airborneHours(localTime, takeoff) : null,
      ground_speed_kts: speed ? readingFrom(speed, lowest) : null,
      east: round2(at(channels.east)),
      north: round2(at(channels.north)),
    },
  };
}

/**
 * The take-off times of a flight for its breach: the daily entry's as the crew typed it (the flight entry of the same
 * day and flight number, and aircraft when both name one), and the log's.
 */
export function heightBreachTakeoffs(
  flight: Pick<Flight, 'date' | 'flt_num' | 'aircraft_id' | 'takeoff_local'>,
  entries: readonly Pick<
    DailyEntry,
    'date' | 'flt_num' | 'aircraft_id' | 'entry_type' | 'takeoff_time' | 'deleted_at'
  >[],
): { readonly entryTakeoff: string | null; readonly logTakeoff: string | null } {
  const entry = entries.find(
    (e) =>
      e.deleted_at === null &&
      e.entry_type === 'flight' &&
      e.date === flight.date &&
      e.flt_num === flight.flt_num &&
      (!e.aircraft_id || !flight.aircraft_id || e.aircraft_id === flight.aircraft_id),
  );
  return { entryTakeoff: entry?.takeoff_time ?? null, logTakeoff: flight.takeoff_local ?? null };
}

// ---------------------------------------------------------------------------------------- the words

/** The line as the report (and the Zdas log, TJR) writes it: `L100460` → `10046.0`, `T190050` → `19005.0`. */
export function breachLineText(lineName: string): string {
  return qcLogLine(lineName.split(':')[0]!).replace(/^[A-Z]/, '');
}

const NOT_RECORDED = 'not recorded';
/** The zFid range is kept in one piece (no-break spaces). */
const NBSP = '\u00a0';
const fixed = (v: number | null, d: number): string => (v === null ? NOT_RECORDED : v.toFixed(d));
/** Metres as MB writes them: `40m`, `37.5m`. */
const metres = (v: number): string => `${Number(v.toFixed(2))}m`;

/** Which height a breach is below, in the report's words: "Hard Deck 30m" (MB's label, R196). */
export function breachHeightWords(kind: HeightBreachKind, thresholdM: number): string {
  return `${qcChannelLabel(kind === 'hard_deck' ? 'DTM_plus_Hard_Deck_HT' : 'DTM_LWH_HT')} ${metres(thresholdM)}`;
}

/**
 * The entry's three lines of facts, as MB decided them (4 Nov 2026): the day, flight, line, duration, distance and
 * minimum; the local time, time since airborne and ground speed; the position and the breach's zFid range (no-break
 * spaces keep it in one piece). A hard deck breach says so before the duration.
 */
export function heightBreachEntryLines(f: HeightBreachFacts): [string, string, string] {
  const deck = f.kind === 'hard_deck' ? `${breachHeightWords('hard_deck', f.threshold_m)}, ` : '';
  const first =
    `Day ${f.jday} Flt${String(f.flight_number).padStart(3, '0')}, Line: ${breachLineText(f.line_name)} ` +
    `(${deck}Duration: ${f.duration_fids} Fids, Distance: ${Math.round(f.distance_m)}m, Min: ${f.min_m.toFixed(2)}m)`;
  const airborne = f.airborne_h === null ? NOT_RECORDED : `${f.airborne_h.toFixed(1)}hrs`;
  const speed = f.ground_speed_kts === null ? NOT_RECORDED : `${f.ground_speed_kts.toFixed(1)} kts`;
  const second = `Local Time: ${f.local_time ?? NOT_RECORDED}, Time Since Airborne: ${airborne}, Ground Speed: ${speed}`;
  const zfids = zfidRangeText(f.fid_start, f.fid_end).replace(/ /g, NBSP);
  const third = `(East: ${fixed(f.east, 2)} North: ${fixed(f.north, 2)}) ${zfids}`;
  return [first, second, third];
}

/** `Reason – <reason>` (MB's en dash). */
export function heightBreachReasonLine(reason: string): string {
  return `Reason – ${reason.trim()}`;
}

/** What is wrong with a reason as typed, or null. */
export function heightBreachReasonProblem(reason: string): string | null {
  const r = reason.trim();
  if (r === '') return 'Type the reason for the breach.';
  if (r.length > BREACH_REASON_MAX)
    return `The reason is ${r.length} characters; at most ${BREACH_REASON_MAX}.`;
  return null;
}

/** A person's initials as MB signs the report: "Michael Brayne" → "MB". */
export function personInitials(name: string): string {
  return name
    .trim()
    .split(/[\s.-]+/)
    .filter((p) => /^\p{L}/u.test(p))
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

/** A name part as a file name takes it: spaces to underscores, characters a file name cannot hold left out. */
const fileNamePart = (s: string): string =>
  s
    .trim()
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

/**
 * The job's name in the report's file name and "Job:" heading (decision 1017, Q894 2 B): the project number, client
 * and area of Project settings, `<number>_<client>_<area>` with spaces as underscores ("F26044", "IGO Ltd",
 * "Kimberley WA" give `F26044_IGO_Ltd_Kimberley_WA`); those left empty are left out. Only when all three are empty,
 * the project folder's own name; `fromFolder` says so (the form tells the person to fill in Project settings).
 */
export function heightBreachJobName(
  project: Pick<Project, 'number' | 'client_name' | 'area'> & { readonly folder_path?: string | null },
): { readonly job: string; readonly fromFolder: boolean } {
  const parts = [project.number, project.client_name, project.area].map((p) => fileNamePart(p ?? ''));
  const job = parts.filter((p) => p !== '').join('_');
  if (job !== '') return { job, fromFolder: false };
  const folder =
    (project.folder_path ?? '')
      .replace(/[\\/]+$/, '')
      .split(/[\\/]/)
      .pop() ?? '';
  return { job: fileNamePart(folder) || 'Project', fromFolder: true };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function dateParts(isoDate: string): [string, string, string] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) throw new RangeError(`${isoDate} is not a date (yyyy-mm-dd)`);
  return [m[1]!, m[2]!, m[3]!];
}

/** `<job>_Working_Height_Breach_Report_03112026` for 3 Nov 2026. */
export function heightBreachFileStem(job: string, isoDate: string): string {
  const [y, mo, d] = dateParts(isoDate);
  return `${job}${BREACH_REPORT_SUFFIX}${d}${mo}${y}`;
}

/** `Updated: 03-Nov-2026 MB`. */
export function heightBreachUpdatedText(isoDate: string, initials: string): string {
  const [y, mo, d] = dateParts(isoDate);
  return `Updated: ${d}-${MONTHS[Number(mo) - 1]!}-${y}${initials ? ` ${initials}` : ''}`;
}

/**
 * The heading: `Job: F26044_IGO_Ltd_Kimberley_WA– Radalt less than 40m AGL reasons` (MB's, his dash kept). The
 * height is the lower working height of the report's newest such entry; a report with hard deck breaches names the
 * hard deck too ("Radalt less than 40m AGL and Hard Deck 30m reasons").
 */
export function heightBreachHeading(
  job: string,
  entries: readonly Pick<HeightBreachFacts, 'kind' | 'threshold_m'>[],
  fallback: { readonly lowerWorkingM: number | null; readonly hardDeckM: number | null },
): string {
  const newest = (k: HeightBreachKind): number | null =>
    [...entries].reverse().find((e) => e.kind === k)?.threshold_m ?? null;
  const lwh = newest('lower_working_height') ?? fallback.lowerWorkingM;
  const deck = newest('hard_deck');
  const below = lwh !== null ? `Radalt less than ${metres(lwh)} AGL` : 'Radalt below the working heights';
  const and = deck !== null ? ` and ${breachHeightWords('hard_deck', deck)}` : '';
  return `Job: ${job}– ${below}${and} reasons`;
}

/** An entry kept in Teslyn Field (`height_breach`, Field SQLite 0075). */
export interface HeightBreachEntry {
  readonly id: string;
  readonly project_id: string;
  readonly run_id: string;
  readonly result_id: string;
  readonly flight_id: string | null;
  readonly test_key: HeightBreachKind;
  readonly line_name: string;
  readonly fid_start: number;
  readonly fid_end: number;
  readonly facts: HeightBreachFacts;
  readonly reason: string;
  readonly picture_sha256: string;
  /** Earlier reasons: `[{ reason, changed_at, changed_by_name }]`. */
  readonly history: readonly {
    readonly reason: string;
    readonly changed_at: string;
    readonly changed_by_name: string;
  }[];
  readonly created_at: string;
  readonly created_by_name: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
  readonly deleted_reason: string | null;
  readonly deleted_by_name: string | null;
  /** Who changed it last in Teslyn Cloud (decision 1017, Field 0076); null for a change made on a laptop. */
  readonly updated_by?: string | null;
}

/** The manifest Teslyn Field's core renders into the Word document and the PDF. */
export interface HeightBreachManifest {
  readonly schema: typeof BREACH_REPORT_SCHEMA;
  readonly heading: string;
  readonly updated: string;
  readonly file_stem: string;
  /** In the report's order: earlier entries first. `picture`: the entry's id (its plot is kept under it). */
  /** `lines`: the three lines of facts and the reason line. */
  readonly entries: readonly {
    readonly picture: string;
    readonly lines: readonly [string, string, string, string];
  }[];
}

/** The whole report as of `today` (yyyy-mm-dd): every entry not removed, oldest first. */
export function heightBreachManifest(input: {
  readonly job: string;
  readonly entries: readonly HeightBreachEntry[];
  readonly today: string;
  readonly initials: string;
  readonly lowerWorkingM: number | null;
  readonly hardDeckM: number | null;
}): HeightBreachManifest {
  const kept = input.entries
    .filter((e) => e.deleted_at === null)
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
  return {
    schema: BREACH_REPORT_SCHEMA,
    heading: heightBreachHeading(
      input.job,
      kept.map((e) => e.facts),
      { lowerWorkingM: input.lowerWorkingM, hardDeckM: input.hardDeckM },
    ),
    updated: heightBreachUpdatedText(input.today, input.initials),
    file_stem: heightBreachFileStem(input.job, input.today),
    entries: kept.map((e) => {
      const [a, b, c] = heightBreachEntryLines(e.facts);
      return { picture: e.id, lines: [a, b, c, heightBreachReasonLine(e.reason)] as const };
    }),
  };
}

/** The entry already kept for a breach (same flight, line, test and zFid start), not removed. */
export function heightBreachEntryFor(
  entries: readonly HeightBreachEntry[],
  b: {
    readonly flight_id: string | null;
    readonly line_name: string;
    readonly test_key: string;
    readonly fid_start: number;
  },
): HeightBreachEntry | null {
  return (
    entries.find(
      (e) =>
        e.deleted_at === null &&
        (e.flight_id ?? '') === (b.flight_id ?? '') &&
        e.line_name === b.line_name &&
        e.test_key === b.test_key &&
        e.fid_start === b.fid_start,
    ) ?? null
  );
}

/**
 * Who reads the Height breach report's entries in Teslyn Cloud (decision 1017: the project's staff, never clients or
 * pilots; RLS height_breach_select, 20261104000000).
 */
export const HEIGHT_BREACH_READ_ROLES: readonly string[] = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
];

export function canReadHeightBreaches(roles: readonly string[]): boolean {
  return roles.some((r) => HEIGHT_BREACH_READ_ROLES.includes(r));
}

/** The report's entries for reading in Teslyn Cloud: those in the report oldest first, then those removed. */
export function heightBreachesForReading(entries: readonly HeightBreachEntry[]): {
  readonly current: readonly HeightBreachEntry[];
  readonly removed: readonly HeightBreachEntry[];
} {
  const order = (a: HeightBreachEntry, b: HeightBreachEntry): number =>
    a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id);
  return {
    current: entries.filter((e) => e.deleted_at === null).sort(order),
    removed: entries.filter((e) => e.deleted_at !== null).sort(order),
  };
}
